import { useState, useEffect } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Grid,
  Autocomplete,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Chip,
  Stack,
  Tooltip,
  Breadcrumbs,
  Link as MuiLink,
  Alert,
  Snackbar,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import { Link, useNavigate } from "react-router-dom";
import useFetchObjects from "../../api/useFetchObjects";
import {
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  LocalMall as LocalMallIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  getFormDirection,
  getRtlTextFieldProps,
} from "../utils/rtlForm";

import { formatJalaliDate } from "../utils/dateFormat";
const PurchaseReturnAdd = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [reason, setReason] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t, i18n } = useTranslation();
  const formDirection = getFormDirection(i18n);
  const { fieldDirection, fieldTextAlign } = formDirection;

  // Fetch purchases with filters
  const queryParams = new URLSearchParams();
  if (searchTerm) queryParams.append('search', searchTerm);

  const {
    data: purchasesDataRaw = [],
    isLoading,
    isError,
    refetch: refetchPurchases,
  } = useFetchObjects(["purchase", searchTerm], `shop/purchases/?${queryParams.toString()}`, token);

  const purchasesData = Array.isArray(purchasesDataRaw?.results) ? purchasesDataRaw.results : (Array.isArray(purchasesDataRaw) ? purchasesDataRaw : []);

  // Fetch suppliers for filter
  const { data: suppliersDataRaw = [] } = useFetchObjects(["suppliers"], "shop/suppliers", token);
  const suppliers = Array.isArray(suppliersDataRaw?.results) ? suppliersDataRaw.results : (Array.isArray(suppliersDataRaw) ? suppliersDataRaw : []);

  // Fetch current stock data with auto-refresh
  const { data: stockDataRaw = [], refetch: refetchStock } = useFetchObjects(
    ["stock", selectedPurchase?.id], // Add dependency to refetch when purchase changes
    "shop/stocks/", 
    token
  );

  const stockData = Array.isArray(stockDataRaw?.results) ? stockDataRaw.results : (Array.isArray(stockDataRaw) ? stockDataRaw : []);

  // Auto-refresh stock every 2 seconds when purchase is selected
  useEffect(() => {
    if (selectedPurchase) {
      const interval = setInterval(() => {
        refetchStock();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [selectedPurchase, refetchStock]);

  // Dynamic stock state for multiple products
  const [productStocks, setProductStocks] = useState({});
  
  // Get stock from existing stockData (remove hardcoded values)
  const getRealStock = (productName) => {
    const stock = stockData.find(s => 
      s.purchase_item_details?.product?.name === productName
    );
    return stock ? stock.quantity : 0;
  };
  
  // Update stock after successful return
  const updateStockAfterReturn = (productName, returnQty) => {
    setProductStocks(prev => ({
      ...prev,
      [productName]: Math.max(0, (prev[productName] || getRealStock(productName)) - returnQty)
    }));
  };

  // Simple calculation for remaining quantity using product stock
  const getRemainingQty = (item, returnQty) => {
    const stock = getRealStock(item.purchase_item_details?.product_details?.name);
    return Math.max(0, stock - returnQty);
  };

  // Add The data into purchase-returns
  const { handleAdd, loading, resetForm } = useAdd("shop/purchase-returns", token);

  // Handle purchase selection
  const handlePurchaseSelect = (event, purchase) => {
    if (!purchase) {
      setSelectedPurchase(null);
      setReturnItems([]);
      return;
    }

    setSelectedPurchase(purchase);
    setReturnItems([]);
    
    // Refresh stock data when purchase is selected
    refetchStock();
    
    if (!purchase.purchase_items || purchase.purchase_items.length === 0) {
      setAlert({
        open: true,
        message: "This purchase has no items to return",
        severity: "warning"
      });
    }
  };

  // Toggle item selection with quantity restoration on delete
  const toggleItemSelection = (item) => {
    setReturnItems((prevItems) => {
      const existingIndex = prevItems.findIndex((i) => i.purchase_item === item.id);
      if (existingIndex >= 0) {
        // Item is being removed - add back the quantity to available stock
        const removedItem = prevItems[existingIndex];
        const productName = removedItem.purchase_item_details?.product_details?.name;
        if (productName) {
          setProductStocks(prev => ({
            ...prev,
            [productName]: (prev[productName] || getRealStock(productName)) + removedItem.return_quantity
          }));
        }
        return prevItems.filter((_, index) => index !== existingIndex);
      } else {
        const price = Number(item.buy_price || item.purchase_price || 0);
        return [
          ...prevItems,
          {
            purchase_item: item.id,
            return_quantity: 1,
            refund_amount: (price * 1).toFixed(2),
            isCustomRefund: false,
            purchase_item_details: item,
          },
        ];
      }
    });
  };

  // Handle quantity change and update available stock immediately
  const handleQuantityChange = (itemId, value) => {
    const maxQty = returnItems.find((item) => item.purchase_item === itemId)?.purchase_item_details?.quantity || 0;
    const quantity = Math.max(0, Math.min(Number(value) || 0, maxQty));

    setReturnItems((prevItems) =>
      prevItems.map((item) => {
        if (item.purchase_item === itemId) {
          const price = Number(item.purchase_item_details.buy_price || item.purchase_item_details.purchase_price || 0);
          const refund = item.isCustomRefund ? item.refund_amount : (quantity * price).toFixed(2);
          
          // Update available stock immediately when quantity changes
          const productName = item.purchase_item_details?.product_details?.name;
          if (productName) {
            const currentStock = productStocks[productName] || getRealStock(productName);
            const previousReturn = item.return_quantity || 0;
            const newStock = currentStock + previousReturn - quantity; // Add back previous, subtract new
            setProductStocks(prev => ({
              ...prev,
              [productName]: Math.max(0, newStock)
            }));
          }
          
          return {
            ...item,
            return_quantity: quantity,
            refund_amount: refund,
          };
        }
        return item;
      })
    );
  };

  // Handle refund amount change
  const handleRefundChange = (itemId, value) => {
    setReturnItems((prevItems) =>
      prevItems.map((item) => {
        if (item.purchase_item === itemId) {
          return {
            ...item,
            refund_amount: Number(value || 0).toFixed(2),
            isCustomRefund: true,
          };
        }
        return item;
      })
    );
  };

  // Calculate total refund amount
  const totalRefundAmount = returnItems
    .reduce((total, item) => total + Number(item.refund_amount || 0), 0)
    .toFixed(2);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedPurchase) {
      setAlert({ open: true, message: "Please select a purchase", severity: "error" });
      return;
    }

    if (returnItems.length === 0) {
      setAlert({ open: true, message: "Please select at least one item to return", severity: "error" });
      return;
    }

    if (!reason.trim()) {
      setAlert({ open: true, message: "Please provide a reason for return", severity: "error" });
      return;
    }

    const formData = {
      purchase: selectedPurchase.id,
      shop: selectedPurchase.shop_details?.id || selectedPurchase.shop,
      reason: reason.trim(),
      return_items: returnItems.map((item) => ({
        purchase_item: item.purchase_item,
        return_quantity: item.return_quantity,
        refund_amount: item.refund_amount,
        // Add product info for stock update
        product_id: item.purchase_item_details.product_details?.id,
        current_stock: item.purchase_item_details.product_details?.stock_quantity || 0
      })),
    };

    handleAdd(formData, {
      onSuccess: async () => {
        // Update local stock immediately
        returnItems.forEach(item => {
          const productName = item.purchase_item_details?.product_details?.name;
          if (productName) {
            updateStockAfterReturn(productName, item.return_quantity);
          }
        });
        
        setAlert({ open: true, message: "Purchase return created and stock updated!", severity: "success" });
        setTimeout(() => {
          resetForm();
          window.location.reload();
        }, 1500);
      },
      onError: (error) => {
        setAlert({ open: true, message: error.message || "Failed to create purchase return", severity: "error" });
      }
    });
  };

  // Check if an item is selected for return
  const isItemSelected = (itemId) => {
    return returnItems.some((item) => item.purchase_item === itemId);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSupplierFilter("");
    setDateFilter("");
    setSelectedPurchase(null);
    setReturnItems([]);
  };

  return (
    <Box sx={{ p: 3, direction: fieldDirection }}>
      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <MuiLink component={Link} to="/shop" color="inherit" underline="hover">
          <LocalMallIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Branch
        </MuiLink>
        <MuiLink component={Link} to="/shop/purchase-return" color="inherit" underline="hover">
          <ReceiptIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t("PurchaseReturnIndex.index_title")}
        </MuiLink>
        <Typography color="text.primary">
          <AttachMoneyIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t("PurchaseReturnIndex.form_createTitle")}
        </Typography>
      </Breadcrumbs>

      {/* Main Card */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", direction: fieldDirection }}>
              <Typography variant="h5" fontWeight="bold">
                {t("PurchaseReturnIndex.form_createTitle")}
              </Typography>
              <Button component={Link} to="/shop/purchase-return" variant="outlined" startIcon={<ArrowBackIcon />}>
                Back to Returns
              </Button>
            </Box>

            <Divider />

            {/* Purchase Selection Form */}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Purchase Search */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={purchasesData || []}
                    loading={isLoading}
                    getOptionLabel={(option) => {
                      const totalAmount = option.total_amount || 
                        (option.purchase_items?.reduce((sum, item) => 
                          sum + (Number(item.buy_price || 0) * Number(item.quantity || 0)), 0
                        )) || 0;
                      
                      return `Purchase #${option.id} - ${
                        option.supplier_details?.name || "N/A"
                      } - ${formatJalaliDate(option.purchase_date)} - ${
                        option.currency_details?.title || ""
                      } ${Number(totalAmount).toFixed(2)}`;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("PurchaseReturnIndex.form_searchPurchase")}
                        variant="outlined"
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <LocalMallIcon color="action" sx={{ mr: 1 }} />,
                          endAdornment: (
                            <>
                              {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        {...getRtlTextFieldProps(formDirection, {
                          inputProps: params.inputProps,
                          InputLabelProps: params.InputLabelProps,
                        })}
                      />
                    )}
                    onChange={handlePurchaseSelect}
                    value={selectedPurchase}
                    fullWidth
                  />
                </Grid>

                {/* Reason */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("PurchaseReturnIndex.form_returnReason")}
                    variant="outlined"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <InfoIcon color="action" sx={{ mr: 1 }} />,
                    }}
                    {...getRtlTextFieldProps(formDirection)}
                  />
                </Grid>
              </Grid>

              {/* Purchase Details */}
              {selectedPurchase && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textAlign: fieldTextAlign }}>
                    {t("PurchaseReturnIndex.form_purchaseInfo")}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
                    <Chip label={`Purchase ID: #${selectedPurchase.id}`} color="primary" variant="outlined" />
                    <Chip label={`Date: ${formatJalaliDate(selectedPurchase.purchase_date)}`} />
                    <Chip label={`Supplier: ${selectedPurchase.supplier_details?.name || "N/A"}`} />
                    <Chip
                      label={`Total: ${selectedPurchase.currency_details?.title || ""} ${Number(
                        selectedPurchase.total_amount || 
                        selectedPurchase.purchase_items?.reduce((sum, item) => 
                          sum + (Number(item.buy_price || 0) * Number(item.quantity || 0)), 0
                        ) || 0
                      ).toFixed(2)}`}
                      color="success"
                    />
                  </Stack>
                </Box>
              )}

              {/* Purchase Items Table */}
              {selectedPurchase && selectedPurchase.purchase_items && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textAlign: fieldTextAlign }}>
                    {t("PurchaseReturnIndex.form_selectItems")} ({selectedPurchase.purchase_items.length} items)
                  </Typography>
                  <Paper sx={{ overflow: "hidden" }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>{t("PurchaseReturnIndex.table_product")}</TableCell>
                            <TableCell>{t("PurchaseReturnIndex.table_unit")}</TableCell>
                            <TableCell align="right">{t("PurchaseReturnIndex.table_price")}</TableCell>
                            <TableCell align="right">{t("PurchaseReturnIndex.table_soldQty")}</TableCell>
                            <TableCell align="right">Available Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedPurchase.purchase_items.map((item) => (
                            <TableRow
                              key={item.id}
                              hover
                              onClick={() => toggleItemSelection(item)}
                              sx={{ cursor: "pointer" }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox color="primary" checked={isItemSelected(item.id)} />
                              </TableCell>
                              <TableCell>{item.product_details?.name || "N/A"}</TableCell>
                              <TableCell>{item.unit_details?.title || "N/A"}</TableCell>
                              <TableCell align="right">
                                {Number(item.buy_price || item.purchase_price || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">{item.quantity}</TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={getRealStock(item.product_details?.name)} 
                                  color={getRealStock(item.product_details?.name) > 0 ? "success" : "error"}
                                  size="small"
                                  sx={{ minWidth: 50 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}

              {/* Selected Items for Return */}
              {returnItems.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ textAlign: fieldTextAlign }}>
                    {t("PurchaseReturnIndex.form_returnSummary")} ({returnItems.length} items selected)
                  </Typography>
                  <Paper>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("PurchaseReturnIndex.table_product")}</TableCell>
                            <TableCell>{t("PurchaseReturnIndex.table_unit")}</TableCell>
                            <TableCell align="right">{t("PurchaseReturnIndex.table_price")}</TableCell>
                            <TableCell align="right">{t("PurchaseReturnIndex.table_returnQty")}</TableCell>
                            <TableCell align="right">{t("PurchaseReturnIndex.table_refundAmount")}</TableCell>
                            <TableCell align="center">{t("PurchaseReturnIndex.table_actions")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {returnItems.map((item) => (
                            <TableRow key={item.purchase_item}>
                              <TableCell>{item.purchase_item_details?.product_details?.name || "N/A"}</TableCell>
                              <TableCell>{item.purchase_item_details?.unit_details?.title || "N/A"}</TableCell>
                              <TableCell align="right">
                                {Number(item.purchase_item_details?.buy_price || item.purchase_item_details?.purchase_price || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  size="small"
                                  value={item.return_quantity}
                                  onChange={(e) => handleQuantityChange(item.purchase_item, e.target.value)}
                                  inputProps={{
                                    min: 1,
                                    max: getRealStock(item.purchase_item_details?.product_details?.name),
                                  }}
                                  {...getRtlTextFieldProps(formDirection, {
                                    inputProps: {
                                      min: 1,
                                      max: getRealStock(item.purchase_item_details?.product_details?.name),
                                    },
                                    sx: { width: "100px" },
                                  })}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  variant="outlined"
                                  size="small"
                                  value={item.refund_amount}
                                  onChange={(e) => handleRefundChange(item.purchase_item, e.target.value)}
                                  {...getRtlTextFieldProps(formDirection, {
                                    inputProps: { min: 0, step: 0.01 },
                                    sx: { width: "120px" },
                                  })}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={t("PurchaseReturnIndex.tooltip_removeItem")}>
                                  <IconButton
                                    color="error"
                                    onClick={() => toggleItemSelection(item.purchase_item_details)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", direction: fieldDirection }}>
                    <Typography variant="h6">
                      {t("PurchaseReturnIndex.form_totalRefund")}:{" "}
                      <Box component="span" fontWeight="bold" color="success.main">
                        {selectedPurchase?.currency_details?.title || ""} {totalRefundAmount}
                      </Box>
                    </Typography>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={loading || returnItems.length === 0 || !reason.trim()}
                      sx={{ minWidth: 180 }}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AttachMoneyIcon />}
                    >
                      {loading ? t("PurchaseReturnIndex.form_processing") : t("PurchaseReturnIndex.form_submit")}
                    </Button>
                  </Box>
                </Box>
              )}
            </form>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseReturnAdd;
